/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { Form } from 'react-bootstrap'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useAuth } from '../../auth/core/Auth'
import { MidTermAdditionRequest } from '../../auth/core/_models'

// Define the number of days dynamically
const validationDaysLimit = 30;

const registrationSchema = Yup.object().shape({
  Name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(100, 'Maximum 50 symbols')
    .required('Name is required'),
  Dob: Yup.date()
    .required('Date of Birth Required'),
  Gender: Yup.string()
    .required('Gender Required'),
  Relation: Yup.string()
    .required('Relation Required'),
  DateOfJoin: Yup.date()
    .required('Date of Join Required')
    .test(
      'dobOrDaysLimit',
      `Date of Join should be greater than or equal to Date of Birth and within the past ${validationDaysLimit} days`,
      function (value) {
        const dob = new Date(this.resolve(Yup.ref('Dob')));
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - validationDaysLimit);

        if (!value) {
          return false;
        }
        if (value < dob) {
          return false;
        }

        return value >= daysAgo;
      }
    )
    .max(new Date(), "Date of Join should be less than or equal to current date"),
  ProofDocument: Yup.mixed()
    .nullable()
    .test('fileSize', 'File size must be less than 5MB', (value) => {
      if (!value) return true; // Allow null/empty
      return (value as File).size <= 5 * 1024 * 1024; // 5MB
    })
    .test('fileType', 'Only PDF, JPG, JPEG, PNG files are allowed', (value) => {
      if (!value) return true; // Allow null/empty
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      return allowedTypes.includes((value as File).type);
    })
});

export function MidTermForm() {
  const initialValues = {
    Name: '',
    Dob: '',
    Gender: '',
    Relation: '',
    DateOfJoin: '',
    ProofDocument: null as File | null
  }
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { currentUser } = useAuth()
  const EmployeeId = currentUser?.EmployeeId!
  const CrmClientId = currentUser?.CRMClientId!
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setSuccessMessage(null); // Reset success message
      try {
        let data: MidTermAdditionRequest = {
          name: values.Name!,
          dateOfBirth: values.Dob?.toString()!,
          relationId: values.Relation!,
          gender: values.Gender!,
          dateOfJoin: values.DateOfJoin?.toString(),
          crmClientId: CrmClientId,
          EmployeeId: EmployeeId,
        };

        // Only add proof document fields if a file is uploaded
        if (values.ProofDocument) {
          const fileToBase64 = (file: File): Promise<string> => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = error => reject(error);
            });
          };

          const base64File = await fileToBase64(values.ProofDocument);
          data.proofDocument = base64File;
          data.documentName = values.ProofDocument.name;
          data.documentType = values.ProofDocument.type;
        }

        const response = await fetch(
          'https://etl.futurisk.in/webhook/159b8d04-c401-4e60-85db-51387dab92e2',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(currentUser?.sessioncode ? { SessionCode: currentUser.sessioncode } : {}),
            },
            body: JSON.stringify(data),
          }
        );
        if (!response.ok) {
          throw new Error('Failed to submit data');
        }
        setSuccessMessage('Data submitted successfully');
        setLoading(false);
        formik.resetForm(); // Reset the form after successful submission
      } catch (error) {
        console.error(error);
        setStatus('Submission failed. Please try again.');
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik.values.Relation === '1') {
      formik.setFieldValue('DateOfJoin', formik.values.Dob);
    }
  }, [formik.values.Relation, formik.values.Dob]);

  return (
    <>
      {successMessage && (
        <div className='mb-lg-15 alert alert-success'>
          <div className='alert-text font-weight-bold'>{successMessage}</div>
        </div>
      )}
      {!successMessage && (
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_pristyncare_form'
          onSubmit={formik.handleSubmit}
        >
          {/* begin::Heading */}
          <div className=''>
            {/* begin::Title */}
            <h1 className='text-dark mb-3 '> </h1>
            <div style={{
              backgroundColor: '#fff8e1',
              border: '1px solid #fbc02d',
              padding: '16px 24px',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              borderRadius: '8px',
              marginBottom: '24px',
              color: '#333'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Important Instructions:</div>
              <ul style={{
                paddingLeft: '20px',
                margin: 0,
                listStyleType: 'disc'
              }}>
                <li style={{ marginBottom: '6px' }}>
                  This form is only for adding a <strong>newly married spouse</strong> or a <strong>newborn baby</strong>.
                </li>
                <li>
                  Such additions must be reported <strong>within {validationDaysLimit} days</strong> of the <strong>marriage date</strong> or the <strong>baby's date of birth</strong>.
                </li>
                <li>
                Please note, These additions will only be covered under your health insurance post confirmation by the HR team. 
                </li>
              </ul>
            </div>
            {/* end::Title */}
          </div>
          {/* end::Heading */}

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          {/* begin::Form group Firstname */}
          <div className='fv-row mb-7'>
            <label className='form-label fw-bolder text-dark fs-6'>Name</label>
            <input
              placeholder='Name'
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('Name')}
              className={clsx(
                'form-control  form-control-solid',
                {
                  'is-invalid': formik.touched.Name && formik.errors.Name,
                },
                {
                  'is-valid': formik.touched.Name && !formik.errors.Name,
                }
              )}
            />
            {formik.touched.Name && formik.errors.Name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.Name}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group city */}
          <div className='fv-row mb-7'>
            <label className='form-label fw-bolder text-dark fs-6'>Relation</label>
            <Form.Select
              size='lg'
              className='form-select form-select-solid form-select-lg fw-bold'
              {...formik.getFieldProps('Relation')}
            >
              <option value=''>Select Relation</option>
              <option key={1} value={'1'}>Child</option>
              <option key={2} value={'2'}>Spouse</option>
            </Form.Select>
          </div>
          {/* end : Form group */}

          {/* begin::Form group Dob */}
          {formik.values.Relation && (
            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Dob</label>
              <input
                placeholder='Dob'
                type='date'
                autoComplete='off'
                {...formik.getFieldProps('Dob')}
                className={clsx(
                  'form-control  form-control-solid',
                  {
                    'is-invalid': formik.touched.Dob && formik.errors.Dob,
                  },
                  {
                    'is-valid': formik.touched.Dob && !formik.errors.Dob,
                  }
                )}
              />
              {formik.touched.Dob && formik.errors.Dob && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.Dob}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* end::Form group */}

          {/* begin::Form group city */}
          {formik.values.Relation && (
            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>Gender</label>
              <Form.Select
                size='lg'
                className='form-select form-select-solid form-select-lg fw-bold'
                {...formik.getFieldProps('Gender')}
              >
                <option value=''>Select Gender</option>
                <option key={'1'} value={'Male'}>Male</option>
                <option key={'2'} value={'Female'}>Female</option>
              </Form.Select>
            </div>
          )}
          {/* end : Form group */}

          {/* begin::Form group Proof Document */}
          {formik.values.Relation && (
            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>
                {formik.values.Relation === '2' ? 'Marriage Certificate or Invitation' : 'Birth Certificate or Discharge Summary'}
              </label>
              <input
                type='file'
                accept='.pdf,.jpg,.jpeg,.png'
                placeholder='Upload your document'
                title='Upload your proof document'
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  formik.setFieldValue('ProofDocument', file);
                }}
                className={clsx(
                  'form-control form-control-solid',
                  {
                    'is-invalid': formik.touched.ProofDocument && formik.errors.ProofDocument,
                  },
                  {
                    'is-valid': formik.touched.ProofDocument && !formik.errors.ProofDocument,
                  }
                )}
              />
              <div className='form-text'>Upload PDF, JPG, JPEG, or PNG file (Max 5MB) - Optional</div>
              {formik.touched.ProofDocument && formik.errors.ProofDocument && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.ProofDocument}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* end::Form group */}

          {/* begin::Form group Dob */}
          {formik.values.Relation && (
            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>
                {formik.values.Relation === '2' ? 'Date of Marriage' : 'Date Of Join'}
              </label>
              <input
                placeholder={formik.values.Relation === '2' ? 'Date of Marriage' : 'Date Of Join'}
                type='date'
                autoComplete='off'
                {...formik.getFieldProps('DateOfJoin')}
                disabled={formik.values.Relation === '1'} // Lock for "Child"
                min={new Date(new Date().setDate(new Date().getDate() - validationDaysLimit)).toISOString().split('T')[0]}
                max={new Date().toISOString().split('T')[0]}
                className={clsx(
                  'form-control  form-control-solid',
                  {
                    'is-invalid': formik.touched.DateOfJoin && formik.errors.DateOfJoin,
                  },
                  {
                    'is-valid': formik.touched.DateOfJoin && !formik.errors.DateOfJoin,
                  }
                )}
              />
              {formik.touched.DateOfJoin && formik.errors.DateOfJoin && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.DateOfJoin}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Submit</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Form group */}
        </form>
      )}
    </>
  )
}
